import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import {
	CloseOrigin,
	ToastData,
	ToastTypes,
} from '../components/blackrock/advisor-center/toast/model';
import { createMainNavToast } from '../cms-components/blackrock/advisor-center/header/_helpers/mainNavToaster';
import { isSsr } from './isSsr';

declare global {
	interface Window {
		OnetrustActiveGroups?: string;
		OneTrust?: {
			OnConsentChanged?: (handler: ConsentChangedHandler) => void;
			ToggleInfoDisplay?: () => void;
		};
	}
}

const COOKIE_TOASTER_TIMEOUT = 7000;
export const NO_COOKIES_ACCEPTED_STATUS_CODE = 451;
export const NO_COOKIES_ACCEPTED_ERROR = 'NO_COOKIES_ACCEPTED_ERROR';
export const NO_COOKIES_ACCEPTED_ERROR_MSG =
	'Cookies required for this request are not accepted';

export enum CookieCategory {
	NECESSARY = 'C0001',
	PREFERENCE = 'C0003',
	STATISTICAL_ANALYTICS = 'C0002',
	MARKETING_TRACKING_PROFILING = 'C0004',
	CONVERSATIONAL = 'C0011',
}

type OnConsentChangedEvent = CustomEvent<CookieCategory[]>;
type ConsentChangedHandler = (e: OnConsentChangedEvent) => void;

export type CookieToasterTranslation = {
	manageCookiesCtaLabel: string;
	manageCookiesCtaAriaLabel: string;
	warningLabel: string;
	warningAriaLabel: string;
	closeButtonAriaLabel: string;
};

export const isCookieAllowed = (cookieCategory: CookieCategory) => {
	const oneTrustActiveGroups = window.top?.OnetrustActiveGroups;
	return oneTrustActiveGroups?.includes(cookieCategory) || false;
};

export const useIsCookieAllowed = (
	cookieCategory: CookieCategory
): [boolean, Dispatch<SetStateAction<boolean>>] => {
	const [cookieAllowed, setCookieAllowed] = useState<boolean>(
		isSsr() ? false : isCookieAllowed(cookieCategory)
	);

	useEffect(() => {
		const consentChangedHandler = (event: OnConsentChangedEvent) => {
			const activeGroups = event.detail;
			setCookieAllowed(activeGroups.includes(cookieCategory));
		};
		window.top?.OneTrust?.OnConsentChanged?.(consentChangedHandler);
	}, []);

	return [cookieAllowed, setCookieAllowed];
};

export const createCookieToaster = (
	translation: CookieToasterTranslation | undefined,
	ctaDataLinkEvent?: string
) => {
	if (!translation) {
		console.error('Missing translation for cookie toaster');
		return;
	}

	const lastFocused = document?.activeElement as HTMLElement;

	const toastData: ToastData = {
		type: ToastTypes.Warning,
		label: translation.warningLabel || '',
		ariaLabel: translation.warningAriaLabel,
		closeButtonAriaLabel: translation.closeButtonAriaLabel,
		lifetime: COOKIE_TOASTER_TIMEOUT,
		ctaLabel: translation.manageCookiesCtaLabel,
		ctaAriaLabel: translation.manageCookiesCtaAriaLabel,
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		ctaCallback: () => window.top.OneTrust.ToggleInfoDisplay(),
		ctaDataLinkEvent: ctaDataLinkEvent,
		focused: true,
		focusPrev: () => lastFocused.focus(),
		focusNext: () => lastFocused.focus(),
		onClose: (closeOrigin: CloseOrigin) => {
			if (closeOrigin !== CloseOrigin.LIFETIME_FOCUS_HAS_LEFT) {
				lastFocused.focus();
			}
		},
	};

	createMainNavToast(toastData);
};
